import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const errMsg = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join(', ') || fallback;
  if (d && typeof d === 'object') return d.msg || fallback;
  return fallback;
};

const ChangePasswordDialog = ({ trigger, buttonClassName = '', dark = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setLoading(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error('New password must be at least 6 characters');
    if (next !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      toast.success(data.message || 'Password changed successfully');
      setOpen(false);
      reset();
    } catch (error) {
      toast.error(errMsg(error, 'Could not change password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: () => setOpen(true) })
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          className={`rounded-full ${dark ? 'text-white hover:bg-white/15' : ''} ${buttonClassName}`}
          title="Change password"
          data-testid="change-password-button"
        >
          <KeyRound className="w-4 h-4" />
          <span className="ml-1 hidden lg:inline">Password</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Enter your current password, then choose a new one.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cp-current">Current password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="cp-current" type="password" className="pl-10" value={current}
                  onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-new">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="cp-new" type="password" className="pl-10" value={next}
                  onChange={(e) => setNext(e.target.value)} required data-testid="cp-new-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-confirm">Confirm new password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input id="cp-confirm" type="password" className="pl-10" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11"
              data-testid="cp-submit-button">
              {loading ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChangePasswordDialog;
